import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, Calendar, HelpCircle, ChevronDown, ChevronUp, MapPin, TrendingUp, ExternalLink, Bookmark, Map } from 'lucide-react';
import DistrictMap from '../components/DistrictMap';
import InfoTooltip from '../components/InfoTooltip';
import Footer from '../components/Footer';
import DevelopmentBanner from '../components/DevelopmentBanner';
import AppNavbar from '../components/AppNavbar';
import { buildApiUrl } from '../config';
import { supabase } from '../lib/supabaseClient';
import { getMostRecentActionDate } from '../scripts/bills/billUtils';
import { formatDate, getDaysActive } from '../scripts/bills/billUtils';
import { getPolicyTagColor, getPolicyTagIcon } from '../scripts/bills/policyTagUtils';
import { calculatePartisanScore } from '../scripts/bills/partisanScoring';
import { getPartisanDataFromScore } from '../scripts/bills/partisanScoreUtils';

const Homepage = ({
  isDarkMode,
  setIsDarkMode,
  featureControls,
  politicians,
  policyTags,
  getPartyColor,
  getPartyAccentClass,
  getPartyBorderClass,
  getBranchClassification,
  getLatestActivityForTag,
  handlePoliticianClick,
  DEFAULT_VALUES,
  FALLBACK_MESSAGES,
  navigate
}) => {
  const location = useLocation();
  const [selectedTag, setSelectedTag] = useState('all');
  const [billsSearchTerm, setBillsSearchTerm] = useState('');
  const [submittedBillsSearchTerm, setSubmittedBillsSearchTerm] = useState('');
  const [bills, setBills] = useState([]);
  const [billsLoading, setBillsLoading] = useState(true);
  const [expandedBill, setExpandedBill] = useState(null);
  const [savedBills, setSavedBills] = useState(new Set());
  const [favoritedBills, setFavoritedBills] = useState(new Set());
  const [senateDistrictInput, setSenateDistrictInput] = useState('');
  const [houseDistrictInput, setHouseDistrictInput] = useState('');
  const [districtLookup, setDistrictLookup] = useState({ senate: null, house: null });
  const [districtLookupLoading, setDistrictLookupLoading] = useState(false);
  // Address-based district lookup (Geocodio)
  const [addressInput, setAddressInput] = useState('');
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState(null);
  const [addressLookupResult, setAddressLookupResult] = useState(null);
  const [profileTab, setProfileTab] = useState('representatives'); // 'representatives' | 'map'
  const [user, setUser] = useState(null);
  const [savedProfile, setSavedProfile] = useState(null);
  const [savingPolitician, setSavingPolitician] = useState(null);

  // Auth + profile (saved senator/rep ids) for preload and save buttons
  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (!session?.user?.id) {
        setSavedProfile(null);
        return;
      }
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('saved_senator_politician_id, saved_representative_politician_id')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (!error && profile) setSavedProfile(profile);
      else setSavedProfile(null);
    };
    init();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => init());
    return () => subscription?.unsubscribe();
  }, []);

  // Read tag from URL
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Homepage.jsx:44',message:'useEffect reading URL',data:{locationSearch:location.search,currentSelectedTag:selectedTag},timestamp:Date.now(),runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    // URLSearchParams automatically decodes values that were properly encoded with encodeURIComponent
    const tagParam = new URLSearchParams(location.search).get('tag');
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Homepage.jsx:47',message:'After URLSearchParams.get',data:{tagParam,locationSearch:location.search},timestamp:Date.now(),runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    const tag = tagParam || 'all';
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Homepage.jsx:49',message:'Before setSelectedTag in useEffect',data:{tag,tagParam},timestamp:Date.now(),runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    setSelectedTag(tag);
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/2bfe5830-a8b0-4685-ac6d-05a21d4e13f4',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Homepage.jsx:51',message:'After setSelectedTag in useEffect',data:{tag},timestamp:Date.now(),runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
  }, [location.search]);

  // Handle search submit (for form submission, but autosearch is now enabled)
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setSubmittedBillsSearchTerm(billsSearchTerm.trim());
  };

  // Auto-search with debouncing (updates submittedBillsSearchTerm after user stops typing)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSubmittedBillsSearchTerm(billsSearchTerm.trim());
    }, 300); // 300ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [billsSearchTerm]);

  // Fetch bills with server-side search and policy tag filtering
  useEffect(() => {
    const fetchBills = async () => {
      try {
        setBillsLoading(true);
        const params = new URLSearchParams({
          page: '1',
          limit: '100', // Fetch enough to sort and limit display
          sortBy: 'updated_desc', // Sort by most recent
        });
        
        // Add search parameter if present
        if (submittedBillsSearchTerm) {
          params.set('search', submittedBillsSearchTerm);
        }
        
        // Add policy tag parameter if selected
        if (selectedTag !== 'all') {
          params.set('policyTag', selectedTag);
        }
        
        const apiUrl = buildApiUrl(`/api/bills?${params.toString()}`);
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setBills(data.bills || []);
      } catch (error) {
        console.error('Error fetching bills:', error);
        setBills([]);
      } finally {
        setBillsLoading(false);
      }
    };
    fetchBills();
  }, [submittedBillsSearchTerm, selectedTag]);

  // Filter and sort bills (server-side search already applied, just limit display)
  const filteredAndSortedBills = useMemo(() => {
    // Bills are already filtered and sorted by the API
    // Just limit display to 6 bills
    return bills.slice(0, 6);
  }, [bills]);

  // Normalize district input to digits only for API and matching
  const senateDistrictNorm = useMemo(() => (senateDistrictInput.trim().replace(/\D/g, '') || null), [senateDistrictInput]);
  const houseDistrictNorm = useMemo(() => (houseDistrictInput.trim().replace(/\D/g, '') || null), [houseDistrictInput]);
  const hasDistrictSearch = Boolean(senateDistrictNorm || houseDistrictNorm);

  // Fetch legislators by district when user has entered at least one district
  useEffect(() => {
    if (!hasDistrictSearch) {
      setDistrictLookup({ senate: null, house: null });
      return;
    }
    const params = new URLSearchParams();
    if (senateDistrictNorm) params.set('senateDistrict', senateDistrictNorm);
    if (houseDistrictNorm) params.set('houseDistrict', houseDistrictNorm);
    const url = buildApiUrl(`/api/legislators/by-district?${params.toString()}`);
    let cancelled = false;
    setDistrictLookupLoading(true);
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.resolve({ senate: null, house: null })))
      .then((data) => {
        if (cancelled) return;
        const senate = data?.senate ?? null;
        const house = data?.house ?? null;
        setDistrictLookup({ senate, house });
      })
      .catch(() => {
        if (!cancelled) setDistrictLookup({ senate: null, house: null });
      })
      .finally(() => {
        if (!cancelled) setDistrictLookupLoading(false);
      });
    return () => { cancelled = true; };
  }, [hasDistrictSearch, senateDistrictNorm, houseDistrictNorm]);

  // Chamber: prefer API chamber (same source as bills); fallback to branch/office string
  const chamberStr = (p) => ((p.branch || '') + ' ' + (p.office || '')).toLowerCase();
  const isSenator = (p) => {
    const ch = (p.chamber || '').toLowerCase();
    if (ch === 'senate') return true;
    const s = chamberStr(p);
    if (s.includes('house')) return false;
    return s.includes('senate') || (p.office || '').toLowerCase().includes('state senator');
  };
  const isHouseRep = (p) => {
    const ch = (p.chamber || '').toLowerCase();
    if (ch === 'house') return true;
    const s = chamberStr(p);
    if (s.includes('senate')) return false;
    return s.includes('house') || (p.office || '').toLowerCase().includes('state house') || (p.office || '').toLowerCase().includes('state representative');
  };
  const districtMatches = (p, norm) => norm == null ? false : String(p.district ?? '').replace(/\D/g, '') === String(norm);

  // Default: one state senator and one state house rep (by chamber), for when no district search
  const defaultSenator = useMemo(() => politicians.find((p) => isSenator(p)), [politicians]);
  const defaultHouseRep = useMemo(() => politicians.find((p) => isHouseRep(p)), [politicians]);
  const savedSenatorPolitician = useMemo(
    () => (savedProfile?.saved_senator_politician_id && politicians.length
      ? politicians.find((p) => String(p.id) === String(savedProfile.saved_senator_politician_id))
      : null),
    [savedProfile?.saved_senator_politician_id, politicians]
  );
  const savedRepPolitician = useMemo(
    () => (savedProfile?.saved_representative_politician_id && politicians.length
      ? politicians.find((p) => String(p.id) === String(savedProfile.saved_representative_politician_id))
      : null),
    [savedProfile?.saved_representative_politician_id, politicians]
  );

  // When district search is active: build list of items to show (politician card or legislator mini icon)
  const districtDisplayItems = useMemo(() => {
    if (!hasDistrictSearch) return [];
    const items = [];
    if (senateDistrictNorm) {
      const politician = politicians.find((p) => isSenator(p) && districtMatches(p, senateDistrictNorm));
      const legislator = districtLookup.senate;
      if (politician) {
        items.push({ type: 'politician', politician, chamber: 'Senate', districtLabel: `Senate District ${senateDistrictNorm}` });
      } else if (legislator) {
        items.push({ type: 'legislator', legislator, chamber: 'Senate', districtLabel: `Senate District ${senateDistrictNorm}` });
      } else {
        items.push({ type: 'empty', chamber: 'Senate', districtLabel: `Senate District ${senateDistrictNorm}` });
      }
    }
    if (houseDistrictNorm) {
      const politician = politicians.find((p) => isHouseRep(p) && districtMatches(p, houseDistrictNorm));
      const legislator = districtLookup.house;
      if (politician) {
        items.push({ type: 'politician', politician, chamber: 'House', districtLabel: `House District ${houseDistrictNorm}` });
      } else if (legislator) {
        items.push({ type: 'legislator', legislator, chamber: 'House', districtLabel: `House District ${houseDistrictNorm}` });
      } else {
        items.push({ type: 'empty', chamber: 'House', districtLabel: `House District ${houseDistrictNorm}` });
      }
    }
    return items;
  }, [hasDistrictSearch, senateDistrictNorm, houseDistrictNorm, districtLookup, politicians]);

  // Whether any displayed district has no CivicDuty profile (show uh-oh warning)
  const hasRepWithoutProfile = useMemo(() => districtDisplayItems.some((item) => item.type === 'legislator' || item.type === 'empty'), [districtDisplayItems]);

  // Two fixed slots: Senate (left), House (right). When no search, prefer saved senator/rep if logged in; else default; when search, use district results or placeholder.
  const senateSlot = useMemo(() => {
    if (hasDistrictSearch) {
      const item = districtDisplayItems.find((i) => i.chamber === 'Senate');
      if (item) return item;
      return { type: 'placeholder', chamber: 'Senate', message: 'Enter your address above to find your state senator.' };
    }
    if (savedSenatorPolitician) return { type: 'politician', politician: savedSenatorPolitician, chamber: 'Senate' };
    if (defaultSenator) return { type: 'politician', politician: defaultSenator, chamber: 'Senate' };
    return { type: 'placeholder', chamber: 'Senate', message: 'No state senator with a CivicDuty profile in our list yet.' };
  }, [hasDistrictSearch, districtDisplayItems, defaultSenator, savedSenatorPolitician]);

  const houseSlot = useMemo(() => {
    if (hasDistrictSearch) {
      const item = districtDisplayItems.find((i) => i.chamber === 'House');
      if (item) return item;
      return { type: 'placeholder', chamber: 'House', message: 'Enter your address above to find your state representative.' };
    }
    if (savedRepPolitician) return { type: 'politician', politician: savedRepPolitician, chamber: 'House' };
    if (defaultHouseRep) return { type: 'politician', politician: defaultHouseRep, chamber: 'House' };
    return { type: 'placeholder', chamber: 'House', message: 'No state house representative with a CivicDuty profile in our list yet.' };
  }, [hasDistrictSearch, districtDisplayItems, defaultHouseRep, savedRepPolitician]);

  const handleSavePolitician = async (politicianId, chamber) => {
    if (!user?.id) return;
    const col = chamber === 'Senate' ? 'saved_senator_politician_id' : 'saved_representative_politician_id';
    setSavingPolitician(chamber);
    try {
      const { data: existing } = await supabase.from('profiles').select('*').eq('user_id', user.id).maybeSingle();
      const updates = {
        user_id: user.id,
        ...(existing || {}),
        [col]: politicianId
      };
      const { error } = await supabase.from('profiles').upsert(updates, { onConflict: 'user_id' });
      if (error) throw error;
      setSavedProfile((prev) => (prev ? { ...prev, [col]: politicianId } : { [col]: politicianId }));
    } catch (err) {
      console.warn('Failed to save politician:', err);
    } finally {
      setSavingPolitician(null);
    }
  };

  // Handle tag click
  const handleTagClick = (tagName) => {
    // Use object form of navigate to ensure proper URL encoding
    if (tagName === 'all') {
      navigate({ pathname: '/', search: '' });
    } else {
      // Use URLSearchParams to properly construct the query string
      const params = new URLSearchParams();
      params.set('tag', tagName);
      navigate({ pathname: '/', search: `?${params.toString()}` });
    }
  };

  // Handle bill click
  const handleBillClick = (bill) => {
    const cleanBillNumber = bill.bill_number?.replace(/\s+/g, '') || '';
    navigate(`/bills/${cleanBillNumber}`);
  };

  // Handle "See More" button
  const handleSeeMore = () => {
    const params = new URLSearchParams();
    if (submittedBillsSearchTerm) {
      params.set('search', submittedBillsSearchTerm);
    }
    if (selectedTag !== 'all') {
      params.set('policyTag', selectedTag);
    }
    const queryString = params.toString();
    navigate(queryString ? `/bills?${queryString}` : '/bills');
  };

  // Get partisan score badge for a bill (similar to VotingRecords)
  const getPartisanScoreBadge = (bill) => {
    if (bill) {
      // Use stored partisan score from database if available, otherwise calculate it
      const partisanData = (bill.partisan_score !== undefined && bill.partisan_score !== null)
        ? getPartisanDataFromScore(bill.partisan_score)
        : calculatePartisanScore(bill);

      if (partisanData && partisanData.score !== undefined) {
        const score = Math.round(partisanData.score);
        const label = partisanData.label;
        const direction = partisanData.direction;

        // Determine color based on direction and strength
        let colorClass;
        if (direction === 'Democratic') {
          colorClass = isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
        } else if (direction === 'Republican') {
          colorClass = isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700';
        } else {
          // Bipartisan/Neutral
          colorClass = isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800';
        }

        return {
          score,
          label,
          direction,
          colorClass
        };
      }
    }

    // Fallback when no real data is available
    return {
      score: 0,
      label: 'Unknown',
      direction: 'Unknown',
      colorClass: isDarkMode ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-100 text-gray-600'
    };
  };

  // Format date for display
  const formatBillDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <AppNavbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} featureControls={featureControls} />
      <DevelopmentBanner isDarkMode={isDarkMode} />

      {/* Hero Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-4 select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span className="text-[#ef4444]">Civic</span>
            <span className="text-[#3b82f6]">Duty</span>
          </h1>
          <p className={`text-base sm:text-lg md:text-xl lg:text-2xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} max-w-3xl mx-auto px-4`}>
            See what's going on in government, about the issues you care about. Click a policy tag to get started.
          </p>
        </div>
      </section>

      {/* Policy Tags Section */}
      <section className="py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 md:gap-2.5">
              <button
                onClick={() => handleTagClick('all')}
                className={`px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg text-[10px] sm:text-xs md:text-sm font-medium transition-all shadow-sm hover:shadow ${selectedTag === 'all'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-sm'
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 shadow-sm' : 'text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm bg-white'
                  }`}
              >
                All Topics
              </button>
              {policyTags.map(tag => (
                <button
                  key={tag.id || tag._id || tag.name}
                  onClick={() => handleTagClick(tag.name)}
                  className={`px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg text-[10px] sm:text-xs md:text-sm font-medium transition-all whitespace-nowrap shadow-sm hover:shadow ${selectedTag === tag.name
                      ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-sm'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 shadow-sm' : 'text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm bg-white'
                    }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
      </section>

      {/* Two-Column Layout */}
      <section className="py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            
            {/* Left Column: Political Profiles */}
            <div>
              {/* Header */}
              <div className="mb-6">
                <InfoTooltip
                  content="Political Profiles consolidate all official press releases, social media posts, and voting records for elected officials in one easy-to-understand place."
                  isDarkMode={isDarkMode}
                  size="md"
                  underline={false}
                >
                  <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[#ef4444] inline-block cursor-help border-b-2 border-red-300 hover:border-[#ef4444] transition-colors`}>
                    Political Profiles
                  </h2>
                </InfoTooltip>
              </div>

              {/* District lookup: address search */}
              <div className="mb-4">
                <label htmlFor="address-lookup" className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <MapPin className="w-3.5 h-3.5 inline mr-1" />
                  Find your state representatives by address
                </label>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (!addressInput.trim() || addressLookupLoading) return;
                  setAddressLookupLoading(true);
                  setAddressLookupError(null);
                  const url = buildApiUrl(`/api/district-lookup?address=${encodeURIComponent(addressInput.trim())}`);
                  fetch(url)
                    .then(async (res) => {
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.error || 'Lookup failed');
                      return data;
                    })
                    .then((data) => {
                      setAddressLookupResult(data);
                      // Populate district inputs to trigger existing legislator card logic
                      const sd = data.districts?.state_senate?.district_number;
                      const hd = data.districts?.state_house?.district_number;
                      setSenateDistrictInput(sd != null ? String(sd) : '');
                      setHouseDistrictInput(hd != null ? String(hd) : '');
                      // Also set district lookup directly from API response for instant display
                      setDistrictLookup({
                        senate: data.legislators?.senate || null,
                        house: data.legislators?.house || null,
                      });
                    })
                    .catch((err) => {
                      setAddressLookupError(err.message);
                      setAddressLookupResult(null);
                    })
                    .finally(() => setAddressLookupLoading(false));
                }} className="flex gap-2">
                  <input
                    id="address-lookup"
                    type="text"
                    placeholder="e.g. 206 Washington St SW, Atlanta, GA 30334"
                    value={addressInput}
                    onChange={(e) => { setAddressInput(e.target.value); setAddressLookupError(null); }}
                    className={`flex-1 px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'}`}
                  />
                  <button
                    type="submit"
                    disabled={!addressInput.trim() || addressLookupLoading}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                      addressLookupLoading
                        ? 'bg-gray-400 text-white cursor-wait'
                        : 'bg-[#ef4444] text-white hover:bg-[#dc2626]'
                    } disabled:opacity-50`}
                  >
                    {addressLookupLoading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Looking up…</>
                    ) : (
                      <><Search className="w-4 h-4" /> Find</>
                    )}
                  </button>
                </form>
                {addressLookupError && (
                  <p className={`mt-2 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                    {addressLookupError}
                  </p>
                )}
                {addressLookupResult && !addressLookupError && (
                  <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLookupResult.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`underline hover:no-underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    >
                      {addressLookupResult.address}
                    </a>
                    {addressLookupResult.districts?.state_senate && ` · Senate District ${addressLookupResult.districts.state_senate.district_number}`}
                    {addressLookupResult.districts?.state_house && ` · House District ${addressLookupResult.districts.state_house.district_number}`}
                  </p>
                )}
              </div>

              {(districtLookupLoading || addressLookupLoading) && hasDistrictSearch && (
                <div className={`mb-4 py-4 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading representatives…
                </div>
              )}

              {/* Tab bar: Representatives | District Map (only after address lookup) */}
              {addressLookupResult && (
                <div className={`flex gap-1 mb-4 p-1 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <button
                    onClick={() => setProfileTab('representatives')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                      profileTab === 'representatives'
                        ? isDarkMode ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                        : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Bookmark className="w-4 h-4" /> Representatives
                  </button>
                  <button
                    onClick={() => setProfileTab('map')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                      profileTab === 'map'
                        ? isDarkMode ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                        : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Map className="w-4 h-4" /> District Map
                  </button>
                </div>
              )}

              {/* Tab content: Representatives (default or when tab selected) */}
              {(!addressLookupResult || profileTab === 'representatives') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 md:gap-4 mb-6 items-stretch">
                <div className="min-h-[420px] flex flex-col">
                  {senateSlot.type === 'politician' && (
                    <PoliticianCard
                      key={senateSlot.politician.id}
                      politician={senateSlot.politician}
                      selectedHomepageTag={selectedTag}
                      isDarkMode={isDarkMode}
                      getPartyColor={getPartyColor}
                      getPartyAccentClass={getPartyAccentClass}
                      getPartyBorderClass={getPartyBorderClass}
                      getBranchClassification={getBranchClassification}
                      getLatestActivityForTag={getLatestActivityForTag}
                      handlePoliticianClick={handlePoliticianClick}
                      DEFAULT_VALUES={DEFAULT_VALUES}
                      FALLBACK_MESSAGES={FALLBACK_MESSAGES}
                      {...(user && {
                        onSavePolitician: () => handleSavePolitician(senateSlot.politician.id, 'Senate'),
                        isSaved: String(savedProfile?.saved_senator_politician_id) === String(senateSlot.politician.id),
                        isSaving: savingPolitician === 'Senate',
                        saveLabel: 'Save Senator'
                      })}
                    />
                  )}
                  {senateSlot.type === 'legislator' && (
                    <LegislatorCard
                      legislator={senateSlot.legislator}
                      chamber="Senate"
                      isDarkMode={isDarkMode}
                      getPartyColor={getPartyColor}
                      getPartyAccentClass={getPartyAccentClass}
                      getPartyBorderClass={getPartyBorderClass}
                    />
                  )}
                  {senateSlot.type === 'empty' && (
                    districtLookupLoading && senateDistrictNorm != null
                      ? (
                          <div className={`rounded-lg border p-4 flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading…</p>
                          </div>
                        )
                      : (
                          <div className={`rounded-lg border p-4 flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {senateSlot.districtLabel} does not exist.
                            </p>
                          </div>
                        )
                  )}
                  {senateSlot.type === 'placeholder' && (
                    <div className={`rounded-lg border p-4 flex-1 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{senateSlot.message}</p>
                    </div>
                  )}
                </div>

                <div className="min-h-[420px] flex flex-col">
                  {houseSlot.type === 'politician' && (
                    <PoliticianCard
                      key={houseSlot.politician.id}
                      politician={houseSlot.politician}
                      selectedHomepageTag={selectedTag}
                      isDarkMode={isDarkMode}
                      getPartyColor={getPartyColor}
                      getPartyAccentClass={getPartyAccentClass}
                      getPartyBorderClass={getPartyBorderClass}
                      getBranchClassification={getBranchClassification}
                      getLatestActivityForTag={getLatestActivityForTag}
                      handlePoliticianClick={handlePoliticianClick}
                      DEFAULT_VALUES={DEFAULT_VALUES}
                      FALLBACK_MESSAGES={FALLBACK_MESSAGES}
                      {...(user && {
                        onSavePolitician: () => handleSavePolitician(houseSlot.politician.id, 'House'),
                        isSaved: String(savedProfile?.saved_representative_politician_id) === String(houseSlot.politician.id),
                        isSaving: savingPolitician === 'House',
                        saveLabel: 'Save Representative'
                      })}
                    />
                  )}
                  {houseSlot.type === 'legislator' && (
                    <LegislatorCard
                      legislator={houseSlot.legislator}
                      chamber="House"
                      isDarkMode={isDarkMode}
                      getPartyColor={getPartyColor}
                      getPartyAccentClass={getPartyAccentClass}
                      getPartyBorderClass={getPartyBorderClass}
                    />
                  )}
                  {houseSlot.type === 'empty' && (
                    districtLookupLoading && houseDistrictNorm != null
                      ? (
                          <div className={`rounded-lg border p-4 flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading…</p>
                          </div>
                        )
                      : (
                          <div className={`rounded-lg border p-4 flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {houseSlot.districtLabel} does not exist.
                            </p>
                          </div>
                        )
                  )}
                  {houseSlot.type === 'placeholder' && (
                    <div className={`rounded-lg border p-4 flex-1 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{houseSlot.message}</p>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Tab content: District Map */}
              {addressLookupResult && profileTab === 'map' && (
                <div className="mb-6">
                  <DistrictMap
                    coordinates={addressLookupResult.coordinates}
                    districts={addressLookupResult.districts}
                    isDarkMode={isDarkMode}
                  />
                </div>
              )}

              {/* Uh-oh warning: shown when user looked up a district and their rep doesn't have a CivicDuty profile */}
              {hasDistrictSearch && hasRepWithoutProfile && (
                <div className={`mb-4 rounded-lg p-4 border ${isDarkMode ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}>
                    Your representative doesn&apos;t have a CivicDuty profile yet.
                  </p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-amber-100/90' : 'text-amber-800'}`}>
                    If you&apos;d like to see their information in one place, contact them and ask them to request a CivicDuty profile. When they sign up, you&apos;ll see their full profile here.
                  </p>
                </div>
              )}

              {/* CTA: constituent-facing + legislator-facing */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-red-50'} rounded-lg p-4 border ${isDarkMode ? 'border-gray-700' : 'border-red-200'}`}>
                <div className="flex items-start gap-2 mb-3">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex-1`}>
                    Want your representative&apos;s full profile on CivicDuty? Contact them and ask them to request a profile—we will consolidate all of their official social media links and campaign links into this profile alongside filling out their about page.
                  </p>
                  <InfoTooltip
                    content="Our long-term goal is to create Political Profiles for all legislators in the assembly, but due to our beta status and small team size, we can only work with legislators who have reached out to us directly."
                    isDarkMode={isDarkMode}
                    size="md"
                  >
                    <HelpCircle className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} cursor-help flex-shrink-0 mt-0.5`} />
                  </InfoTooltip>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                  For legislators, please see <Link to="/about#for-legislators" className="underline font-medium text-[#ef4444] hover:text-[#dc2626]">here</Link>. Legislators and staff: email us from your official address to request your profile.
                </p>
                <button
                  className="px-4 py-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors font-medium"
                  onClick={() => window.open('mailto:founders@civicduty.app?subject=Request Access for Political Profile', '_blank')}
                >
                  Request Access
                </button>
              </div>
            </div>

            {/* Right Column: Bills */}
            <div>
              {/* Header */}
              <div className="mb-6">
                <InfoTooltip
                  content="Bills page displays all legislative bills with their current status, voting records, and policy tags to help you track what's happening in the legislature."
                  isDarkMode={isDarkMode}
                  size="md"
                  underline={false}
                >
                  <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[#3b82f6] inline-block cursor-help border-b-2 border-blue-300 hover:border-[#3b82f6] transition-colors`}>
                    Bills
                  </h2>
                </InfoTooltip>
              </div>

              {/* Search Bar */}
              <form className="mb-6" onSubmit={handleSearchSubmit}>
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} w-5 h-5`} />
                  <input
                    type="text"
                    placeholder="Search by bill number, title, or description..."
                    value={billsSearchTerm}
                    onChange={(e) => setBillsSearchTerm(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                      }`}
                  />
                </div>
              </form>

              {/* Bills List */}
              {billsLoading ? (
                <div className="text-center py-8">
                  <div className={`w-8 h-8 border-4 ${isDarkMode ? 'border-blue-600' : 'border-blue-600'} border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading bills...</p>
                </div>
              ) : filteredAndSortedBills.length === 0 ? (
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} text-center`}>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                    {submittedBillsSearchTerm && selectedTag !== 'all'
                      ? `No bills found matching "${submittedBillsSearchTerm}" with tag "${selectedTag}".`
                      : submittedBillsSearchTerm
                        ? `No bills found matching "${submittedBillsSearchTerm}".`
                        : selectedTag !== 'all'
                          ? `No bills found for "${selectedTag}".`
                          : 'No bills found.'
                    }
                  </p>
                  {(submittedBillsSearchTerm || selectedTag !== 'all') && (
                    <button
                      onClick={() => {
                        const params = new URLSearchParams();
                        if (submittedBillsSearchTerm) {
                          params.set('search', submittedBillsSearchTerm);
                        }
                        if (selectedTag !== 'all') {
                          params.set('policyTag', selectedTag);
                        }
                        navigate(`/bills?${params.toString()}`);
                      }}
                      className="px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors font-medium"
                    >
                      View all results on Bills page
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2.5 md:space-y-3 h-[500px] sm:h-[600px] md:h-[700px] overflow-y-auto">
                  {filteredAndSortedBills.map((bill, index) => {
                    // Helper to get latest action date excluding "Effective Date" (frontend-only fix)
                    const getMostRecentActionDateExcludingEffective = (bill) => {
                      if (!bill.history || bill.history.length === 0) {
                        return getMostRecentActionDate(bill);
                      }
                      // Find first action that is NOT "Effective Date"
                      const nonEffectiveAction = bill.history.find(action => {
                        const actionLower = (action.action || '').toLowerCase();
                        const code = action.code || '';
                        return code !== 'EFF' && !actionLower.includes('effective date');
                      });
                      return nonEffectiveAction?.date || getMostRecentActionDate(bill);
                    };
                    const mostRecentDate = getMostRecentActionDateExcludingEffective(bill);
                    const formattedDate = formatBillDate(mostRecentDate);
                    const partisanData = getPartisanScoreBadge(bill);
                    const billTitle = bill.display_title || bill.title || `Bill ${bill.bill_number}`;

                    return (
                      <div
                        key={bill.bill_number || index}
                        onClick={() => setExpandedBill(expandedBill === index ? null : index)}
                        className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden transition-all duration-200 hover:shadow-lg relative cursor-pointer`}
                      >
                        <div className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex items-start md:items-center md:space-x-4 flex-1">
                              <div className="flex-1 md:pl-0 pl-1">
                                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                                  <span className={`font-bold text-sm md:text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {bill.bill_number}
                                  </span>
                                  {formattedDate && (
                                    <span className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {formattedDate}
                                    </span>
                                  )}
                                  <span className={`text-[11px] md:text-xs px-2 py-0.5 md:py-1 rounded font-medium ${partisanData.colorClass}`}>
                                    {partisanData.label}
                                  </span>
                                </div>
                                <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mt-1 line-clamp-2`}>
                                  {billTitle}
                                </p>

                                {/* Policy Tags */}
                                {bill.policy_tags && bill.policy_tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {bill.policy_tags.map((tag, tagIdx) => {
                                      const IconComponent = getPolicyTagIcon(tag);
                                      const tagColor = getPolicyTagColor(tag, isDarkMode);
                                      return (
                                        <span key={tagIdx} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm`}>
                                          <IconComponent className="w-3 h-3 mr-1" />
                                          {tag}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 self-start md:self-auto">
                              {/* Mobile: icon-only View Bill button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBillClick(bill);
                                }}
                                className={`p-2 rounded-lg text-xs font-medium transition-all md:hidden ${isDarkMode
                                    ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                aria-label="View Bill"
                                title="View Bill"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                              {/* Desktop: labeled View Bill button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBillClick(bill);
                                }}
                                className={`hidden md:flex px-3 py-1 rounded-lg text-xs font-medium transition-all ${isDarkMode
                                    ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  } items-center space-x-1`}
                              >
                                <ExternalLink className="w-3 h-3" />
                                <span>View Bill</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedBill(expandedBill === index ? null : index);
                                }}
                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                              >
                                {expandedBill === index ? (
                                  <ChevronUp className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                                ) : (
                                  <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                                )}
                              </button>
                            </div>
                          </div>

                          {expandedBill === index && (
                            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <div className="text-sm">
                                <span className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Summary:
                                </span>
                                <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {bill.ai_summary || 'No summary found'}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* "See More" Button */}
              <div className="mt-6">
                <button
                  onClick={handleSeeMore}
                  className="w-full px-4 py-3 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors font-medium"
                >
                  View all results on Bills page
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spacing before footer */}
      <div className="pb-12 md:pb-16"></div>

      {/* Footer */}
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
};

// PoliticianCard component (reused from App.jsx)
const PoliticianCard = ({
  politician,
  selectedHomepageTag,
  isDarkMode,
  getPartyColor,
  getPartyAccentClass,
  getPartyBorderClass,
  getBranchClassification,
  getLatestActivityForTag,
  handlePoliticianClick,
  DEFAULT_VALUES,
  FALLBACK_MESSAGES,
  // Optional: show save icon in top-right (e.g. state senator/rep on homepage)
  onSavePolitician,
  isSaved,
  isSaving,
  saveLabel
}) => {
  const savedAriaLabel = saveLabel === 'Save Senator' ? 'Saved as your state senator' : 'Saved as your state representative';
  const ariaLabel = isSaving ? saveLabel : (isSaved ? savedAriaLabel : saveLabel);
  const title = ariaLabel;
  const [latestActivity, setLatestActivity] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const formatServerActivitySummary = (summary, tags) => {
      if (!summary || typeof summary !== 'string') return summary;
      const hasVotingTag = Array.isArray(tags) && tags.includes('Voting Record');
      const voteDashRegex = /^Voted\s+([A-Za-z ]+)\s+[—-]\s+(.+)\s*$/i;
      const match = summary.match(voteDashRegex);
      if (hasVotingTag && match) {
        const voteText = match[1].trim();
        const title = match[2].trim();
        return `Voted "${voteText}" on ${title}`;
      }
      return summary;
    };

    const fetchLatestActivity = () => {
      setLoading(true);
      try {
        const activity = getLatestActivityForTag(politician.id, selectedHomepageTag);
        if (activity) {
          setLatestActivity(activity);
        } else {
          const summary = politician.recentActivity;
          const date = politician.recentActivityDate;
          const tags = Array.isArray(politician.recentActivityTags) ? politician.recentActivityTags : [];
          const tagMatches =
            selectedHomepageTag === 'all' ||
            (tags && tags.includes(selectedHomepageTag));

          if (summary && tagMatches) {
            const formattedSummary = formatServerActivitySummary(summary, tags);
            const formattedDate = date
              ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
              : null;
            setLatestActivity({
              id: `fallback-${politician.id}`,
              type: tags.includes('Voting Record') ? 'vote' : 'fallback',
              summary: formattedSummary,
              date: date || null,
              formattedDate,
              tags
            });
          } else {
            setLatestActivity(null);
          }
        }
      } catch (error) {
        console.error('Error fetching latest activity:', error);
        setLatestActivity(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestActivity();
  }, [politician.id, selectedHomepageTag, getLatestActivityForTag]);

  return (
    <div
      onClick={() => handlePoliticianClick(politician)}
      className={`group relative h-full min-h-[420px] flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer ${politician.party === 'Democrat'
          ? isDarkMode ? 'hover:border-blue-800' : 'hover:border-blue-200'
          : politician.party === 'Republican'
            ? isDarkMode ? 'hover:border-red-800/50' : 'hover:border-red-200'
            : isDarkMode ? 'hover:border-gray-600' : 'hover:border-gray-200'
        } border-2 border-transparent`}
    >
      {saveLabel && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSavePolitician?.(); }}
          disabled={isSaving}
          className={`absolute top-3 right-3 p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} shadow-sm disabled:opacity-60`}
          aria-label={ariaLabel}
          title={title}
        >
          {isSaved ? (
            <svg
              viewBox="0 0 24 24"
              className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M6 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16l-6-3.5L6 20V4z" />
            </svg>
          ) : (
            <Bookmark className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          )}
        </button>
      )}
      <div className="p-4 md:p-6 flex flex-col flex-1 min-h-0">
        <div className="flex flex-col items-center text-center flex-shrink-0">
          <img
            src={politician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(politician.name)}&background=random&color=fff`}
            alt={politician.name}
            className={`w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 mb-3 ${getPartyBorderClass(politician.party)}`}
          />
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${getPartyColor(politician.party)}`}>
            {politician.party}
          </span>
          <h3 className={`text-base md:text-lg font-semibold mb-1 ${getPartyAccentClass(politician.party)} leading-tight`}>{politician.name}</h3>
          <p className={`text-xs md:text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {politician.chamber && String(politician.chamber).toLowerCase() === 'senate' ? 'Georgia State Senator' : 'State Representative of Georgia'}
          </p>
          <div className={`flex items-center justify-center text-sm md:text-base mb-4 ${getPartyAccentClass(politician.party)}`}>
            <MapPin className="w-4 h-4 md:w-5 md:h-5 mr-1.5" />
            <span className="font-semibold leading-tight">
              {politician.district
                ? (/^district\s+/i.test(String(politician.district).trim())
                    ? String(politician.district).trim()
                    : `District ${String(politician.district).trim()}`)
                : FALLBACK_MESSAGES.NO_LOCATION_INFO}
            </span>
          </div>
        </div>

        <div
          className={`mt-3 md:mt-4 p-3 md:p-4 rounded-lg border transition-colors flex-1 min-h-[180px] flex flex-col ${politician.party === 'Democrat'
              ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-800 hover:from-blue-900/50 hover:to-blue-800/30' : 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-100 hover:from-blue-100 hover:to-blue-200/50'
              : politician.party === 'Republican'
                ? isDarkMode ? 'bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-800 hover:from-red-900/50 hover:to-red-800/30' : 'bg-gradient-to-br from-red-50 to-red-100/50 border-red-100 hover:from-red-100 hover:to-red-200/50'
                : isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
            } ${latestActivity && (latestActivity.type === 'vote' || (latestActivity.tags || []).includes('Voting Record')) ? 'cursor-pointer' : ''}`}
          onClick={(e) => {
            const isVoting = latestActivity && (latestActivity.type === 'vote' || (latestActivity.tags || []).includes('Voting Record'));
            if (!isVoting) return;
            e.stopPropagation();
            const tagToApply = selectedHomepageTag !== DEFAULT_VALUES.ALL ? selectedHomepageTag : undefined;
            handlePoliticianClick(politician, { viewMode: 'voting', tag: tagToApply });
          }}
        >
          {/* Header - stacked vertically */}
          <div className="mb-3">
            <div className="flex items-center mb-2">
              <TrendingUp className={`w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 ${getPartyAccentClass(politician.party)}`} />
              <span className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="hidden sm:inline">Recent Updates</span>
                <span className="sm:hidden">Updates</span>
              </span>
              {loading && (
                <div className={`ml-2 w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin`}></div>
              )}
            </div>
            
            {/* Date - stacked below header */}
            {latestActivity && !loading && (
              <div className={`flex items-center text-[10px] sm:text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                <span className="whitespace-nowrap">{latestActivity.formattedDate}</span>
              </div>
            )}
          </div>
          
          {/* Content */}
          {loading ? (
            <div className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-tight`}>
              Loading activity...
            </div>
          ) : latestActivity ? (
            <div>
              <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-3 leading-relaxed`}>
                {latestActivity.summary}
              </p>
              {/* Policy Tags */}
              {latestActivity.tags && Array.isArray(latestActivity.tags) && latestActivity.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {latestActivity.tags.map((tag, tagIdx) => {
                    const IconComponent = getPolicyTagIcon(tag);
                    const tagColor = getPolicyTagColor(tag, isDarkMode);
                    return (
                      <span key={tagIdx} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm`}>
                        <IconComponent className="w-2.5 h-2.5 mr-1" />
                        {tag}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-tight`}>
              {selectedHomepageTag === DEFAULT_VALUES.ALL ? FALLBACK_MESSAGES.NO_RECENT_ACTIVITY : `${FALLBACK_MESSAGES.NO_RECENT_ACTIVITY_IN_TAG} ${selectedHomepageTag}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Legislator card (no CivicDuty profile): same layout as PoliticianCard but with GGA link instead of Recent Updates
const LegislatorCard = ({
  legislator,
  chamber,
  isDarkMode,
  getPartyColor,
  getPartyAccentClass,
  getPartyBorderClass
}) => {
  const chamberLabel = chamber === 'Senate' ? 'Georgia State Senator' : 'State Representative of Georgia';
  const districtNum = legislator.districtNumber ?? legislator.district_number;
  const photoUrl = legislator.photoUrl ?? legislator.photo_url;
  const party = legislator.party || '';
  const ch = (legislator.chamber || chamber || '').toString().toLowerCase();
  const ggaPath = ch === 'senate' ? 'senate' : 'house';
  const ggaMemberId = legislator.ggaMemberId ?? legislator.gga_member_id;
  const ggaUrl = ggaMemberId
    ? `https://www.legis.ga.gov/members/${ggaPath}/${ggaMemberId}`
    : null;

  return (
    <div
      className={`group relative h-full min-h-[420px] flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm border-2 border-transparent`}
    >
      <div className="p-4 md:p-6 flex flex-col flex-1 min-h-0">
        <div className="flex flex-col items-center text-center flex-shrink-0">
          <img
            src={photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(legislator.name)}&background=random&color=fff`}
            alt={legislator.name}
            className={`w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 mb-3 ${getPartyBorderClass(party)}`}
          />
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${getPartyColor(party)}`}>
            {party || '—'}
          </span>
          <h3 className={`text-base md:text-lg font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} leading-tight`}>{legislator.name}</h3>
          <p className={`text-xs md:text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {chamberLabel}
          </p>
          <div className={`flex items-center justify-center text-sm md:text-base mb-4 ${getPartyAccentClass ? getPartyAccentClass(party) : (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`}>
            <MapPin className="w-4 h-4 md:w-5 md:h-5 mr-1.5" />
            <span className="font-semibold leading-tight">
              {districtNum != null ? `District ${districtNum}` : '—'}
            </span>
          </div>
        </div>

        <div
          className={`mt-3 md:mt-4 p-3 md:p-4 rounded-lg border flex-1 min-h-[180px] flex flex-col ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
        >
          {/* Header - same structure as Recent Updates */}
          <div className="mb-3">
            <div className="flex items-center mb-2">
              <ExternalLink className={`w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Official Page
              </span>
            </div>
          </div>
          {/* Content */}
          <p className={`text-xs md:text-sm mb-3 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            This legislator doesn&apos;t have a CivicDuty profile yet.
          </p>
          {ggaUrl ? (
            <a
              href={ggaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-100' : 'bg-white hover:bg-gray-100 text-gray-800 border border-gray-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
              View on Georgia General Assembly
            </a>
          ) : (
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Profile link unavailable</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Homepage;
